import { AAR_DELAY_MS, LASER_OFF_TIMEOUT_MS, WARN_WINDOW_MS } from '../constants.js';
import type { FlightPhase, Timeline } from './types.js';

/** The derived timeline (ARCHITECTURE §5.6). F and L in ms. */
export function deriveTimeline(t0: number, F: number, L: number): Timeline {
  const tImpact = t0 + F;
  const tLaserOn = t0 + F - L;
  return {
    t0,
    F,
    L,
    tImpact,
    tLaserOn,
    tLaserWarn: tLaserOn - WARN_WINDOW_MS,
    tImpactWarn: tImpact - WARN_WINDOW_MS,
    tAar: tImpact + AAR_DELAY_MS,
    tLaserOffTO: tImpact + LASER_OFF_TIMEOUT_MS,
  };
}

export interface Projection {
  phase: FlightPhase;
  /** ms until laser on (negative once passed) */
  toLaserOnMs: number;
  /** ms until impact (negative once passed) */
  toImpactMs: number;
  /** clamp((now − T0)/F, 0, 1) */
  barFraction: number;
  /** position of the laser-on notch on the bar: (F − L)/F */
  notch: number;
  /** inside [tLaserOn − 5 s, tLaserOn) */
  laserWarn: boolean;
  /** inside [tImpact − 5 s, tImpact) */
  impactWarn: boolean;
  /** the big digits: ceil seconds to the next mark (laser on, then impact) */
  digitsS: number;
}

/** The universal rendering rule, every role, every frame (§5.6). */
export function projectTimeline(tl: Timeline, now: number): Projection {
  const toLaserOnMs = tl.tLaserOn - now;
  const toImpactMs = tl.tImpact - now;
  const phase: FlightPhase =
    now < tl.tLaserOn ? 'COUNTDOWN' : now < tl.tImpact ? 'LASER_ON_PHASE' : now < tl.tAar ? 'IMPACT_PHASE' : 'AAR_DUE';
  const barFraction = Math.min(1, Math.max(0, (now - tl.t0) / tl.F));
  const next = phase === 'COUNTDOWN' ? toLaserOnMs : toImpactMs;
  return {
    phase,
    toLaserOnMs,
    toImpactMs,
    barFraction,
    notch: (tl.F - tl.L) / tl.F,
    laserWarn: now >= tl.tLaserWarn && now < tl.tLaserOn,
    impactWarn: now >= tl.tImpactWarn && now < tl.tImpact,
    digitsS: Math.max(0, Math.ceil(next / 1000)),
  };
}
